import { useMemo, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { ApiSidebar, ApiSidebarSection } from '@/components/docs/ApiSidebar';
import { AnchorHeading } from '@/components/docs/AnchorHeading';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Code, Lock, Zap, AlertTriangle, CheckCircle, Menu } from 'lucide-react';
import { CodeBlock } from '@/components/docs/CodeBlock';

const API_BASE_URL = 'https://fxezreosgghqhhgmvbgw.supabase.co/functions/v1/standards-api';

const endpoints = [
  {
    method: 'GET',
    path: '/standards',
    slug: 'list-standards',
    description: 'List all active data standards with metadata',
    response: `{
  "data": [
    {
      "slug": "vehicle-listing",
      "title": "Vehicle Listing",
      "description": "Standard for vehicle listings",
      "version": "1.0.0",
      "category": "vehicle",
      "category_name": "Vehicle",
      "maturity_level": "normative"
    }
  ],
  "meta": {
    "total": 15,
    "version": "1.0.0"
  }
}`,
  },
  {
    method: 'GET',
    path: '/standards/:slug',
    slug: 'get-standard',
    description: 'Get a single standard with full schema, example, and fields',
    params: [{ name: 'slug', type: 'string', required: true, description: 'The unique slug of the standard' }],
    response: `{
  "data": {
    "slug": "vehicle-listing",
    "title": "Vehicle Listing",
    "schema": { ... },
    "example": { ... },
    "fields": [ ... ]
  }
}`,
  },
  {
    method: 'GET',
    path: '/standards/:slug/schema',
    slug: 'get-schema',
    description: 'Get only the JSON Schema for a standard',
    params: [{ name: 'slug', type: 'string', required: true, description: 'The unique slug of the standard' }],
  },
  {
    method: 'GET',
    path: '/standards/:slug/typescript',
    slug: 'get-typescript',
    description: 'Get TypeScript interface definition for a standard',
    params: [{ name: 'slug', type: 'string', required: true, description: 'The unique slug of the standard' }],
    response: `export interface VehicleListing {
  id: string;
  vin: string;
  year: number;
  make: string;
  model: string;
  // ...
}`,
  },
  {
    method: 'GET',
    path: '/standards/:slug/versions',
    slug: 'get-versions',
    description: 'Get version history for a standard',
    params: [{ name: 'slug', type: 'string', required: true, description: 'The unique slug of the standard' }],
  },
  {
    method: 'GET',
    path: '/lookups',
    slug: 'list-lookups',
    description: 'List all available lookup groups',
    response: `{
  "data": [
    { "group": "vehicle_condition", "count": 5 },
    { "group": "fuel_type", "count": 8 }
  ]
}`,
  },
  {
    method: 'GET',
    path: '/lookups/:group',
    slug: 'get-lookup',
    description: 'Get all values for a specific lookup group',
    params: [{ name: 'group', type: 'string', required: true, description: 'The lookup group name' }],
  },
  {
    method: 'POST',
    path: '/validate',
    slug: 'validate-data',
    description: 'Validate data against a standard schema',
    body: `{
  "standardSlug": "vehicle-listing",
  "data": {
    "id": "abc123",
    "vin": "1HGBH41JXMN109186",
    ...
  }
}`,
    response: `{
  "valid": true,
  "errors": []
}`,
  },
];

const methodColors: Record<string, string> = {
  GET: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  POST: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  PUT: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
  DELETE: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
};

export default function ApiDocs() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const sidebarSections: ApiSidebarSection[] = useMemo(() => [
    {
      title: 'API Reference',
      items: [
        { id: 'authentication', title: 'Authentication' },
        { id: 'rate-limits', title: 'Rate Limits' },
        { id: 'endpoints', title: 'Endpoints' },
        // Individual endpoints with indentation
        ...endpoints.map((e) => ({
          id: `endpoint-${e.slug}`,
          title: `${e.method} ${e.path}`,
          indent: true,
        })),
        { id: 'errors', title: 'Error Handling' },
        { id: 'examples', title: 'Code Examples' },
      ],
    },
  ], []);

  return (
    <Layout>
      <SEOHead path="/docs/api" />
      
      <DocsNav />
      
      <div className="container py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <ApiSidebar sections={sidebarSections} className="hidden lg:block" />
          
          {/* Main Content */}
          <main className="flex-1 min-w-0 max-w-4xl">
            {/* Header */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Code className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-3xl font-bold">API Reference</h1>
              </div>
              <p className="text-muted-foreground max-w-2xl">
                The AutomotiveAPI Standards API provides programmatic access to data standards, 
                schemas, and validation. All endpoints return JSON responses.
              </p>
            </div>
            
            {/* Base URL */}
            <Card className="mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Base URL</CardTitle>
              </CardHeader>
              <CardContent>
                <code className="text-sm bg-muted px-3 py-2 rounded-md block overflow-x-auto">
                  {API_BASE_URL}
                </code>
              </CardContent>
            </Card>
            
            {/* Authentication */}
            <section id="authentication" className="mb-10 scroll-mt-24">
              <AnchorHeading level={2} id="authentication" className="mb-4">
                <Lock className="h-5 w-5 mr-2" />
                Authentication
              </AnchorHeading>
              <p className="text-muted-foreground mb-4">
                The API is publicly accessible for read operations. No authentication is required 
                for fetching standards, schemas, or lookups.
              </p>
              <div className="flex items-start gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-700 dark:text-green-400">Public API</p>
                  <p className="text-sm text-muted-foreground">
                    All read endpoints are publicly accessible without an API key.
                  </p>
                </div>
              </div>
            </section>
            
            {/* Rate Limits */}
            <section id="rate-limits" className="mb-10 scroll-mt-24">
              <AnchorHeading level={2} id="rate-limits" className="mb-4">
                <Zap className="h-5 w-5 mr-2" />
                Rate Limits
              </AnchorHeading>
              <p className="text-muted-foreground mb-4">
                To ensure fair usage, the API implements rate limiting:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>100 requests per minute per IP address</li>
                <li>Rate limit headers included in all responses</li>
                <li>429 status code returned when limit exceeded</li>
              </ul>
            </section>
            
            {/* Endpoints */}
            <section id="endpoints" className="mb-10 scroll-mt-24">
              <AnchorHeading level={2} id="endpoints" className="mb-6">
                Endpoints
              </AnchorHeading>
              
              <div className="space-y-6">
                {endpoints.map((endpoint) => (
                  <Card key={endpoint.slug} id={`endpoint-${endpoint.slug}`} className="scroll-mt-24">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <Badge className={methodColors[endpoint.method]} variant="outline">
                          {endpoint.method}
                        </Badge>
                        <code className="text-sm font-mono">{endpoint.path}</code>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {endpoint.description}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {endpoint.params && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Parameters</h4>
                          <div className="bg-muted rounded-md p-3">
                            {endpoint.params.map((param) => (
                              <div key={param.name} className="flex items-start gap-2 text-sm">
                                <code className="text-primary">{param.name}</code>
                                <span className="text-muted-foreground">({param.type})</span>
                                {param.required && <Badge variant="secondary" className="text-xs">required</Badge>}
                                <span className="text-muted-foreground">- {param.description}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {endpoint.body && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Request Body</h4>
                          <CodeBlock code={endpoint.body} language="json" />
                        </div>
                      )}
                      
                      {endpoint.response && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Response</h4>
                          <CodeBlock 
                            code={endpoint.response} 
                            language={endpoint.path.includes('typescript') ? 'typescript' : 'json'} 
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
            
            {/* Error Handling */}
            <section id="errors" className="mb-10 scroll-mt-24">
              <AnchorHeading level={2} id="errors" className="mb-4">
                <AlertTriangle className="h-5 w-5 mr-2" />
                Error Handling
              </AnchorHeading>
              <p className="text-muted-foreground mb-4">
                The API uses standard HTTP status codes and returns error details in JSON format:
              </p>
              <CodeBlock 
                code={`{
  "error": "Standard not found",
  "code": "NOT_FOUND",
  "status": 404
}`}
                language="json"
              />
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">200</Badge>
                  <span className="text-muted-foreground">Success</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">400</Badge>
                  <span className="text-muted-foreground">Bad Request - Invalid parameters</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">404</Badge>
                  <span className="text-muted-foreground">Not Found - Resource doesn't exist</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">429</Badge>
                  <span className="text-muted-foreground">Too Many Requests - Rate limited</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">500</Badge>
                  <span className="text-muted-foreground">Internal Server Error</span>
                </div>
              </div>
            </section>
            
            {/* Code Examples */}
            <section id="examples" className="mb-10 scroll-mt-24">
              <AnchorHeading level={2} id="examples" className="mb-6">
                Code Examples
              </AnchorHeading>
              
              <Tabs defaultValue="curl" className="w-full">
                <TabsList>
                  <TabsTrigger value="curl">cURL</TabsTrigger>
                  <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                  <TabsTrigger value="python">Python</TabsTrigger>
                </TabsList>
                
                <TabsContent value="curl" className="mt-4">
                  <CodeBlock 
                    code={`# List all standards
curl "${API_BASE_URL}/standards"

# Get a specific standard
curl "${API_BASE_URL}/standards/vehicle-listing"

# Get JSON Schema only
curl "${API_BASE_URL}/standards/vehicle-listing/schema"

# Validate data
curl -X POST "${API_BASE_URL}/validate" \\
  -H "Content-Type: application/json" \\
  -d '{"standardSlug": "vehicle-listing", "data": {...}}'`}
                    language="bash"
                  />
                </TabsContent>
                
                <TabsContent value="javascript" className="mt-4">
                  <CodeBlock 
                    code={`// Using fetch
const API_BASE = '${API_BASE_URL}';

// List all standards
const standards = await fetch(\`\${API_BASE}/standards\`)
  .then(res => res.json());

// Get a specific standard
const vehicleListing = await fetch(\`\${API_BASE}/standards/vehicle-listing\`)
  .then(res => res.json());

// Validate data
const validation = await fetch(\`\${API_BASE}/validate\`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    standardSlug: 'vehicle-listing',
    data: { id: 'abc123', vin: '1HGBH41JXMN109186', ... }
  })
}).then(res => res.json());`}
                    language="javascript"
                  />
                </TabsContent>
                
                <TabsContent value="python" className="mt-4">
                  <CodeBlock 
                    code={`import requests

API_BASE = '${API_BASE_URL}'

# List all standards
standards = requests.get(f'{API_BASE}/standards').json()

# Get a specific standard
vehicle_listing = requests.get(f'{API_BASE}/standards/vehicle-listing').json()

# Validate data
validation = requests.post(
    f'{API_BASE}/validate',
    json={
        'standardSlug': 'vehicle-listing',
        'data': {'id': 'abc123', 'vin': '1HGBH41JXMN109186', ...}
    }
).json()`}
                    language="python"
                  />
                </TabsContent>
              </Tabs>
            </section>
          </main>
        </div>
      </div>

      {/* Mobile floating nav */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button size="icon" className="rounded-full shadow-lg h-12 w-12">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle API docs menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 pt-12">
            <h3 className="font-semibold mb-4 px-3">API Reference</h3>
            <ApiSidebar 
              sections={sidebarSections} 
              onItemClick={() => setSidebarOpen(false)}
            />
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
